import { BallType, MachineStatus } from '../enums/machine.enums';

// ! deprecate
export interface IDaemonStatuses {
  AlphaController: MachineStatus;
  BallInserter: MachineStatus;
  PosController: MachineStatus;
  Projector: MachineStatus;
  TiltController: MachineStatus;
  WebsocketInterface: MachineStatus;
  WheelSpeedController: MachineStatus;
  YawController: MachineStatus;
}

export enum DaemonName {
  AlphaController = 'AlphaController',
  BallInserter = 'BallInserter',
  BICamerasController = 'BICamerasController',
  DataCollector = 'DataCollector',
  LightsController = 'LightsController',
  MachineCoordinator = 'MachineCoordinator',
  PosController = 'PosController',
  Projector = 'Projector',
  StereoDetector = 'StereoDetector',
  TiltController = 'TiltController',
  WebsocketInterface = 'WebsocketInterface',
  WheelSpeedController = 'WheelSpeedController',
  YawController = 'YawController',
}

export enum NonDaemonLogger {
  CamerasController = 'CamerasController',
  Configuration = 'Configuration',
  LoggerManager = 'LoggerManager',
  LogUploader = 'LogUploader',
  MediaSaver = 'MediaSaver',
  ParentProcess = 'ParentProcess',
  PitchDetector = 'PitchDetector',
  SpinDetector = 'SpinDetector',
  SystemMonitoring = 'SystemMonitoring',
}

export type LoggerName = DaemonName | NonDaemonLogger;

export const LOGGER_NAMES: LoggerName[] = [
  ...Object.values(DaemonName),
  ...Object.values(NonDaemonLogger),
].sort();

// b/c 'Machine State' is not a daemon, but we include it here for convenience
export type DaemonNameExt = DaemonName | 'Machine State';

// ! deprecate
export interface IDaemonState {
  WebsocketInterface: WebsocketInterface;
  OutputDetector: OutputDetector;
  BallInserter: BallInserter;
  TiltController: TiltController;
  WheelSpeedController: WheelSpeedController;
  YawController: YawController;
  Projector: Projector;
  AlphaController: AlphaController;
  PosController: PosController;
  StereoDetector: StereoDetector;
  BICamerasController: BICamerasController;
  LightsController: LightsController;
  SystemMonitoring?: SystemMonitoring;
}

/* Generic Daemon Interface */
export interface IDaemon<
  TargetMS = Partial<Record<string, any>>,
  CurrComps = Partial<Record<string, any>>,
  ReadyComps = Partial<Record<string, any>>,
  FullState = Partial<Record<string, any>>,
> {
  sender_id: string;
  error_present: boolean;
  error_id: string | null;
  send_time: number;
  RequestID: string | null;
  daemon_status: MachineStatus;
  target_id: string;
  target_ms: TargetMS;
  curr_ms_comps: CurrComps;
  ready_comps: ReadyComps;
  full_state_dict: FullState;
}

interface IEventRecordLog {
  event_id: string;
  sender: string;
  msg_str: string;
  traceback_str: string;
  timestamp: string;
}

export interface IDaemonStateDictBase {
  calibrated: boolean;
  error_present: boolean;
  error_message: string | null;
  event_record_logs: IEventRecordLog[];
}

interface IDaemonMotorStateAlarms {
  alarm_present: boolean;
  alarm_code: string | null;
  readable_alarms: string | null;
}

interface IDaemonMotorState extends IDaemonMotorStateAlarms {
  moving: boolean;
  position: number;
}

/* WebsocketInterface */
interface IWebsocketInterfaceTargetMs {
  w1: number;
  w2: number;
  w3: number;
  a1: number;
  a2: number;
  a3: number;
  qw: number;
  qx: number;
  qy: number;
  qz: number;
  tilt: number;
  yaw: number;
  px: number;
  py: number;
  pz: number;
  training: boolean;
  video_uuid: string;
  od_busy: boolean;
  firing: boolean;
  ball_count: number;
  rapid: boolean;
  model_id: string | null;
  hash: string | null;
  full_hash: string | null;
  matching_hash: string | null;
  ball_type: BallType;
}

interface IWebsocketInterfaceCurrMsComps extends IDaemonStateDictBase {
  firing: boolean;
  network_connected: boolean;
}

interface IWebsocketInterfaceReadyComps {
  firing: boolean;
}

interface IWebsocketInterfaceFullState extends IWebsocketInterfaceCurrMsComps {}

// ! deprecate
type WebsocketInterface = IDaemon<
  IWebsocketInterfaceTargetMs,
  IWebsocketInterfaceCurrMsComps,
  IWebsocketInterfaceReadyComps,
  IWebsocketInterfaceFullState
>;

/* OutputDetector */
interface IOutputDetectorTargetMs {
  od_busy: boolean;
  training: boolean;
  ball_type: string;
}

interface IOutputDetectorCurrMsComps extends IOutputDetectorTargetMs {}
interface IOutputDetectorReadyComps {
  od_busy: boolean;
}
interface IOutputDetectorFullState extends IDaemonStateDictBase {
  od_busy: boolean;
  training: boolean;
  ball_type_str: string;
}

// ! deprecate
type OutputDetector = IDaemon<
  IOutputDetectorTargetMs,
  IOutputDetectorCurrMsComps,
  IOutputDetectorReadyComps,
  IOutputDetectorFullState
>;

/* BallInserter */
interface IBallInserterTargetMs {
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  rapid: boolean;
  training: boolean;
  ball_type: string;
}

interface IBallInserterCurrMsComps {
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  rapid: boolean;
  ball_type: string;
  ball_count: number;
  bi_timeout: boolean;
}

interface IBallInserterReadyComps {
  qx: boolean;
  qy: boolean;
  qz: boolean;
  qw: boolean;
  ball_count: boolean;
}

interface IBallInserterQuat {
  qx: number;
  qy: number;
  qz: number;
  qw: number;
}

interface IBallInserterFullState extends IDaemonStateDictBase {
  bf_motor: IDaemonMotorStateAlarms;
  bi_right: IDaemonMotorStateAlarms;
  bi_left: IDaemonMotorStateAlarms;
  bi_top: IDaemonMotorStateAlarms;
  quat: IBallInserterQuat;
  rapid: boolean;
  ball_count: number;
  training: boolean;
  timed_out: boolean;
  ball_type_str: string;
}

// ! deprecate
type BallInserter = IDaemon<
  IBallInserterTargetMs,
  IBallInserterCurrMsComps,
  IBallInserterReadyComps,
  IBallInserterFullState
>;

/* TiltController */
interface ITiltControllerTargetMs {
  tilt: number;
}

interface ITiltControllerCurrMsComps extends ITiltControllerTargetMs {}
interface ITiltControllerReadyComps {
  tilt: boolean;
}

interface ITiltControllerFullState extends IDaemonStateDictBase {
  tilt_motor: IDaemonMotorState;
  tilt: number;
}

// ! deprecate
type TiltController = IDaemon<
  ITiltControllerTargetMs,
  ITiltControllerCurrMsComps,
  ITiltControllerReadyComps,
  ITiltControllerFullState
>;

/* WheelSpeedController */
interface IWheelSpeedControllerTargetMs {
  w1: number;
  w2: number;
  w3: number;
}

interface IWheelSpeedControllerCurrMsComps extends IWheelSpeedControllerTargetMs {}

interface IWheelSpeedControllerReadyComps {
  w1: boolean;
  w2: boolean;
  w3: boolean;
}

interface IWheelMotorState {
  velocity: number;
  torque: number;
  alarm_present: boolean;
  alarm_code: string | null;
  readable_alarms: string | null;
}

interface IWheelSpeedControllerFullState extends IDaemonStateDictBase {
  w1: IWheelMotorState;
  w2: IWheelMotorState;
  w3: IWheelMotorState;
}

// ! deprecate
type WheelSpeedController = IDaemon<
  IWheelSpeedControllerTargetMs,
  IWheelSpeedControllerCurrMsComps,
  IWheelSpeedControllerReadyComps,
  IWheelSpeedControllerFullState
>;

/* YawController */
interface IYawControllerTargetMs {
  yaw: number;
}

interface IYawControllerCurrMsComps extends IYawControllerTargetMs {}
interface IYawControllerReadyComps {
  yaw: boolean;
}

interface IYawControllerFullState extends IDaemonStateDictBase {
  yaw_motor: IDaemonMotorState;
}

// ! deprecate
type YawController = IDaemon<
  IYawControllerTargetMs,
  IYawControllerCurrMsComps,
  IYawControllerReadyComps,
  IYawControllerFullState
>;

/* Projector */
interface IProjectorTargetMs {
  video_uuid: string;
  px: number;
  pz: number;
}

interface IProjectorCurrMsComps {
  video_uuid: string;
  projector_dt: number;
}

interface IProjectorReadyComps {
  video_uuid: boolean;
}

interface IProjectorFullState extends IDaemonStateDictBase {
  video_uuid: string;
  px: number;
  pz: number;
  projector_dt: number;
}

// ! deprecate
type Projector = IDaemon<
  IProjectorTargetMs,
  IProjectorCurrMsComps,
  IProjectorReadyComps,
  IProjectorFullState
>;

/* AlphaController */
interface IAlphaControllerTargetMs {
  a1: number;
  a2: number;
  a3: number;
}
interface IAlphaControllerCurrMsComps extends IAlphaControllerTargetMs {}
interface IAlphaControllerReadyComps {
  a1: boolean;
  a2: boolean;
  a3: boolean;
}

interface IAlphaControllerFullState extends IDaemonStateDictBase {
  a1: IDaemonMotorState;
  a2: IDaemonMotorState;
  a3: IDaemonMotorState;
}

// ! deprecate
type AlphaController = IDaemon<
  IAlphaControllerTargetMs,
  IAlphaControllerCurrMsComps,
  IAlphaControllerReadyComps,
  IAlphaControllerFullState
>;

/* PosController */
interface IPosControllerTargetMs {
  px: number;
  py: number;
  pz: number;
}
interface IPosControllerCurrMsComps extends IPosControllerTargetMs {}
interface IPosControllerReadyComps {
  px: boolean;
  py: boolean;
  pz: boolean;
}

interface IPosControllerMotorState extends IDaemonMotorState {
  torque: number;
}

interface IPosControllerFullState extends IDaemonStateDictBase {
  x_axis: IDaemonMotorState;
  z_parent: IPosControllerMotorState;
  z_child: IPosControllerMotorState;
  confirm_unloaded: boolean;
  py: number;
}

// ! deprecate
type PosController = IDaemon<
  IPosControllerTargetMs,
  IPosControllerCurrMsComps,
  IPosControllerReadyComps,
  IPosControllerFullState
>;

/* SteroDetector */
interface ISteroDetectorTargetMs {}
interface ISteroDetectorMsComps extends ISteroDetectorTargetMs {}

interface IStereoDetectorReadyComps {}

interface ISteroDetectorFullState extends IDaemonStateDictBase {}

// ! deprecate
type StereoDetector = IDaemon<
  ISteroDetectorTargetMs,
  ISteroDetectorMsComps,
  IStereoDetectorReadyComps,
  ISteroDetectorFullState
>;

/* BICamerasController */
interface IBICamerasControllerTargetMs {}
interface IBICamerasControllerMsComps extends IBICamerasControllerTargetMs {}

interface IBICamerasControllerReadyComps {}

interface IBICamerasControllerFullState extends IDaemonStateDictBase {}

// ! deprecate
type BICamerasController = IDaemon<
  IBICamerasControllerTargetMs,
  IBICamerasControllerMsComps,
  IBICamerasControllerReadyComps,
  IBICamerasControllerFullState
>;

/* LightsController */
interface ILightsControllerTargetMs {}
interface ILightsControllerMsComps extends ILightsControllerTargetMs {}

interface ILightsControllerReadyComps {}

interface ILightsControllerFullState extends IDaemonStateDictBase {}

// ! deprecate
type LightsController = IDaemon<
  ILightsControllerTargetMs,
  ILightsControllerMsComps,
  ILightsControllerReadyComps,
  ILightsControllerFullState
>;

/* SystemMonitoring */
interface ISystemMonitoringTargetMs {}
interface ISystemMonitoringCurrMsComps {
  temp_ambient: number | null;
}
interface ISystemMonitoringReadyComps {}
interface ISystemMonitoringFullState extends IDaemonStateDictBase {
  temp_ambient: number | null;
}

// ! deprecate
type SystemMonitoring = IDaemon<
  ISystemMonitoringTargetMs,
  ISystemMonitoringCurrMsComps,
  ISystemMonitoringReadyComps,
  ISystemMonitoringFullState
>;
